import { useState, useEffect } from "react"

import { EmailInput } from "../inputs/EmailInput";
import { PasswordInput } from "../inputs/PasswordInput";
import { isEmailValid, isPasswordValid } from "../validate";

import { Button, InputOtp } from "@heroui/react";
import { GoogleButton } from "../buttons/GoogleButton";
import { ResendProveIdentityCode } from "../buttons/resendProveIdentityCode";

import { useDispatch } from "react-redux";
import { setLogin, setSubscriptionStatus, setCurrentXp, setSelectedAvatar, setWordCounts } from "../../../state";

import { ResetPassword } from "./ResetPassword";
import { useOnboardingStep } from "../../../hooks/useOnboardingStep.js";


export const Form = () => {
    const { updateOnboardingStep } = useOnboardingStep()
    const dispatch = useDispatch()
    const PRESENTATION_MODE = process.env.REACT_APP_PRESENTATION_MODE === 'true'

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSending, setIsFormSending] = useState(false);

    const [resetPassword, setResetPassword] = useState(false)
    const [serverError, setServerError] = useState("")

    const [verificationRequired, setVerificationRequired] = useState(false)
    const [proveIdentityToken, setProveIdentityToken] = useState("")
    const [proveIdentityCode, setProveIdentityCode] = useState("")
    const [isProvingIdentityLoading, setIsProvingIdentityLoading] = useState(false)

    const [followInstructionsInEmail, setFollowInstructionsInEmail] = useState(false)

    const [canRestore, setCanRestore] = useState(false)
    const [restoreMesage, setRestoreMessage] = useState("")
    const [restoreToken, setRestoreToken] = useState("")
    const [isRestoringLoading, setIsRestoringLoading] = useState("")

    const login = async () => {
        if (PRESENTATION_MODE) {
            setServerError("Sorry, aome stopped its activity. Bye bye")
            return
        }

        setIsFormSending(true)
        setServerError("")
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE}/auth/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify({
                    email,
                    password
                })
            })
            if (response.ok) {

                const data = await response.json()

                if (!data || typeof data !== 'object') {
                    setServerError("ログイン中にエラーが発生しました。もう一度お試しください。")
                    return
                }

                if (data.verificationRequired && data.verificationRequired === true && data.proveIdentityToken) {
                    setVerificationRequired(true)
                    setProveIdentityToken(data.proveIdentityToken)
                    return
                }

                if (data.followInstructionsInEmail && data.followInstructionsInEmail === true) {
                    setFollowInstructionsInEmail(true)
                    return
                }

                if (data.canRestore && data.canRestore === true && data.messageJp && data.restoreToken) {
                    setCanRestore(true)
                    setRestoreMessage(data.messageJp)
                    setRestoreToken(data.restoreToken)
                    return
                }

                if (data.accessToken && data.expiresAt && data.userData && typeof data.userData === 'object') {
                    dispatch(
                        setSubscriptionStatus({subscriptionStatus: data.subscriptionStatus || "trialing"})
                    )
                    dispatch(
                        setCurrentXp({
                            currentXp: data.userData?.currentXp
                        })
                    )
                    dispatch(
                        setSelectedAvatar({
                            selectedAvatar: data.userData?.avatar || 0
                        })
                    )
                    dispatch(
                        setLogin({
                            user: data.userData,
                            token: data.accessToken,
                            expiresAt: data.expiresAt
                        })
                    );
                    dispatch(setWordCounts(data.userData.wordCounts));
                    updateOnboardingStep(data.userData.onboardingStep || "pre-onboarding", false)
                } else {
                    setServerError("ログイン中にエラーが発生しました。もう一度お試しください。")
                }
            } else {
                const data = await response.json()
                setServerError(data?.messageJp ?? "ログイン中にエラーが発生しました。もう一度お試しください。")
            }

        } catch (error) {
            setServerError("ログイン中にエラーが発生しました。もう一度お試しください。")
        } finally {
            setIsFormSending(false)
        }
    }

    const proveIdentity = async () => {
        if (PRESENTATION_MODE) {
            setServerError("Sorry, aome stopped its activity. Bye bye")
            return
        }

        setServerError("")
        setIsProvingIdentityLoading(true)

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE}/auth/prove-identity`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify({
                    proveIdentityToken,
                    proveIdentityCode
                })
            })

            setIsProvingIdentityLoading(false)

            if (response.ok) {
                const { accessToken, expiresAt, userData, subscriptionStatus } = await response.json()

                if (accessToken && expiresAt && userData && typeof userData === 'object') {
                    dispatch(
                        setSubscriptionStatus({
                            subscriptionStatus: subscriptionStatus || "trialing"
                        })
                    )
                    dispatch(
                        setCurrentXp({
                            currentXp: userData?.currentXp
                        })
                    )
                    dispatch(
                        setSelectedAvatar({
                            selectedAvatar: userData?.avatar || 0
                        })
                    )
                    dispatch(
                        setLogin({
                            user: userData,
                            token: accessToken,
                            expiresAt: expiresAt
                        })
                    );
                    dispatch(setWordCounts(userData.wordCounts));

                    updateOnboardingStep(userData.onboardingStep || "pre-onboarding", false)
                } else {
                    setServerError("問題が発生しました。しばらくしてからもう一度お試しください。")
                }
            } else {
                const data = await response.json()
                setServerError(data?.messageJp ?? "問題が発生しました。しばらくしてからもう一度お試しください。")
            }
        } catch (error) {
            setServerError("問題が発生しました。しばらくしてからもう一度お試しください。")
        } finally {
            setIsProvingIdentityLoading(false)
        }
    }

    const restoreAccount = async () => {
        if (PRESENTATION_MODE) {
            setServerError("Sorry, aome stopped its activity. Bye bye")
            return
        }

        setServerError("")
        setIsRestoringLoading(true)

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE}/auth/restore-account`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify({
                    restoreToken,
                })
            })

            setIsRestoringLoading(false)

            if (response.ok) {
                const { accessToken, expiresAt, userData, subscriptionStatus } = await response.json()

                if (accessToken && expiresAt && userData && subscriptionStatus) {
                    dispatch(
                        setSubscriptionStatus({subscriptionStatus: subscriptionStatus || "trialing"})
                    )
                    dispatch(
                        setCurrentXp({
                            currentXp: userData?.currentXp
                        })
                    )
                    dispatch(
                        setSelectedAvatar({
                            selectedAvatar: userData?.avatar || 0
                        })
                    )
                    dispatch(
                        setLogin({
                            user: userData,
                            token: accessToken,
                            expiresAt: expiresAt
                        })
                    );
                    dispatch(setWordCounts(userData.wordCounts));
                    updateOnboardingStep(userData.onboardingStep || "pre-onboarding", false)
                }
            } else {
                const data = await response.json()
                setServerError(data?.messageJp ?? "問題が発生しました。しばらくしてからもう一度お試しください。")
            }
        } catch (error) {
            setServerError("問題が発生しました。しばらくしてからもう一度お試しください。")
        } finally {
            setIsRestoringLoading(false)
        }
    }


    useEffect(() => {
        setIsFormValid(isEmailValid(email) && isPasswordValid(password));
    }, [email, password])


    return (
        <div className="w-full h-auto lg:h-full flex flex-col lg:flex-row gap-2 items-center justify-center">

            {!resetPassword ?

                verificationRequired ?
                    <div className="h-auto min-h-[400px] lg:h-[650px] w-full lg:w-[500px] flex flex-col justify-start lg:justify-center items-center gap-5 px-5 py-10 lg:py-20 lg:px-10">
                        <div className="flex flex-col w-full gap-2 items-center justify-center">
                            <p className="text-xl font-light text-white">本人確認</p>
                            <span className="font-light w-full text-white/90 text-center text-sm">メールに送信された認証コードを入力してください。</span>
                        </div>


                        <div className="w-full flex flex-col items-center justify-center gap-3">

                            <InputOtp
                                size="lg"
                                length={6}
                                value={proveIdentityCode}
                                onValueChange={setProveIdentityCode}
                                classNames={{
                                    segment: "font-light text-base-text bg-base-input/40 backdrop-blur data-[hover=true]:bg-base-input/50 data-[active=true]:bg-base-input/50 group-data-[focus=true]:bg-base-input shadow-none"
                                }}
                            />
                            {serverError &&
                                <div className="w-full flex flex-col items-center justify-center">
                                    <span className="flex items-start gap-2 text-danger text-tiny w-max max-w-full rounded-md bg-danger-50 py-1 px-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-x-icon lucide-circle-x text-danger"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>
                                        {serverError}
                                    </span>
                                </div>
                            }
                            <ResendProveIdentityCode email={email} proveIdentityToken={proveIdentityToken} setProveIdentityToken={setProveIdentityToken} />
                        </div>

                        <Button
                            className="bg-[#27485C] font-light w-full text-white"
                            isDisabled={proveIdentityCode.length !== 6}
                            isLoading={isProvingIdentityLoading}
                            onPress={proveIdentity}
                        >
                            確認
                        </Button>

                    </div>
                    :

                    (followInstructionsInEmail ?
                        <div className="h-auto min-h-[400px] lg:h-[650px] w-full lg:w-[500px] flex flex-col justify-center items-center gap-5 px-5 py-10 lg:py-20 lg:px-10">
                            <p className="text-md font-light text-base-text text-center">ログインを続行するには、メールをご確認ください。</p>
                        </div>
                        :

                        (!canRestore ?
                            <div className="h-auto min-h-[400px] lg:h-[650px] w-full lg:w-[500px] flex flex-col justify-start lg:justify-center items-center gap-5 px-5 py-10 lg:py-20 lg:px-10">
                                {/* <p className="text-xl font-light text-base-text">ログイン</p> */}
                                <div className="flex flex-col items-center">
                                    <p className="text-xl font-light text-white">ログイン</p>
                                </div>


                                <div className="w-full flex flex-col gap-2">
                                    {serverError &&
                                        <div className="w-full">
                                            <span className="flex items-center gap-2 text-danger text-tiny w-max max-w-full rounded-md bg-danger-50 py-1 px-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-x-icon lucide-circle-x text-danger"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>
                                                {serverError}
                                            </span>
                                        </div>
                                    }
                                    <EmailInput email={email} setEmail={setEmail} />
                                    <PasswordInput password={password} setPassword={setPassword} />

                                    <Button
                                        className="bg-[#27485C] font-light w-full text-white"
                                        isDisabled={!isFormValid}
                                        isLoading={isFormSending}
                                        onPress={login}
                                    >
                                        ログイン
                                    </Button>

                                    <span className="text-base-text/30 text-tiny flex flex-col lg:flex-row">
                                        
                                        <a
                                            className="underline underline-offset-6 mr-1 break-keep cursor-pointer text-base-text/70"
                                            onClick={() => setResetPassword(true)}
                                        >
                                            パスワードをお忘れの方はこちら
                                        </a>
                                    </span>
                                </div>

                                <div className="w-full flex gap-2 justify-between items-center">
                                    <hr className="border-none h-[1px] bg-base-text/30 flex-1"></hr>
                                    <span className="font-light w-max text-base-text/70 text-center">または</span>
                                    <hr className="border-none h-[1px] bg-base-text/30 flex-1"></hr>
                                </div>

                                <div className="w-full flex flex-col gap-2 justify-between items-center">
                                    <GoogleButton
                                        setServerError={setServerError}
                                        setCanRestore={setCanRestore}
                                        setRestoreMessage={setRestoreMessage}
                                        setRestoreToken={setRestoreToken}
                                    />
                                </div>


                            </div>
                            :
                            <div className="h-auto min-h-[400px] lg:h-[650px] w-full lg:w-[500px] flex flex-col justify-start lg:justify-center items-center gap-5 px-5 py-10 lg:py-20 lg:px-10">
                                <p className="text-xl font-light text-white">アカウントの復元</p>

                                <p className="text-md font-light text-white">{restoreMesage}</p>
                                <p className="font-light text-danger">{serverError}</p>
                                <div className="flex gap-3 w-full items-center justify-center">
                                    <Button
                                        className="bg-[#27485C] font-light text-white"
                                        variant="flat"
                                        onPress={restoreAccount}
                                        isLoading={isRestoringLoading}
                                    >
                                        アカウントを復元
                                    </Button>
                                    <Button
                                        variant="flat"
                                        className="font-light text-base-text"
                                        onPress={() => setCanRestore(false)}
                                    >
                                        キャンセル
                                    </Button>
                                </div>
                            </div>
                        )
                    )


                :

                <ResetPassword setResetPassword={setResetPassword} />

            }

            {/* <div className="hidden lg:flex lg:h-[650px] w-[50%] p-3">
                <div className="w-full h-full flex items-center justify-center">
                    <img src="landing-page/login-aome.png" className="block h-auto max-w-[380px]" />
                </div>
            </div> */}
        </div>
    )
}